/**
 * ResponseHandler - Handles single pipeline responses from backend.
 * All content flows through SSE (Server-Sent Events).
 *
 * Response format: { streamUrl, jobId, singlePipeline: true }
 *
 * Chunk types handled:
 * - chunk: LLM text streaming (character-by-character)
 * - message: Complete static text (non-LLM, emitted as single block)
 * - component: UI component injection (questionnaire-launcher, action-buttons, etc.)
 * - artifact: Rich content artifacts (treatment plans, insights, charts)
 * - state: Frontend state updates (merged into client state)
 * - action: Client-side action triggers (switch_mode, end_discovery, etc.)
 * - error: Non-critical item failures (logged, does not fail the stream)
 *
 * @module services/ResponseHandler
 */

import { EVENTSOURCE_BASE_URL } from '../core/api.js';

/**
 * Handles unified response format from backend endpoints.
 * Connects to SSE stream and processes typed chunks as they arrive.
 */
export class ResponseHandler {
  /**
   * Creates a ResponseHandler instance.
   * @param {Object} chatManager - ChatManager instance for displaying messages
   * @param {Object} eventBus - EventBus for emitting events
   */
  constructor(chatManager, eventBus) {
    this.chatManager = chatManager;
    this.eventBus = eventBus;
    this.currentJobId = null;
    // Per-job dedupe: highest chunk `seq` already processed. Backend dedupes
    // at the SSE handler, but we keep this as a belt-and-suspenders guard
    // against any future change that might let a duplicate slip through.
    this._lastSeenSeq = -1;
    // Fix B: SSE resilience — track completion so a benign post-COMPLETED
    // close (server closes connection after terminal status) doesn't look
    // like an error, and a no-message watchdog can decide when to give up
    // and fall back to polling.
    this._completedReceived = false;
    this._streamOpenedAt = 0;
    this._lastMessageAt = 0;
    this._watchdogTimer = null;
    this._fallbackTriggered = false;
  }

  /**
   * Reset per-job streaming state at the start of a new stream.
   * @private
   */
  _resetStreamState() {
    this._lastSeenSeq = -1;
    this._completedReceived = false;
    this._streamOpenedAt = Date.now();
    this._lastMessageAt = Date.now();
    this._fallbackTriggered = false;
    if (this._watchdogTimer) {
      clearTimeout(this._watchdogTimer);
      this._watchdogTimer = null;
    }
  }

  /**
   * Handle API response - connects to SSE stream.
   * @param {Object} response - API response object
   * @param {Object} response.data - Response data with streamUrl
   * @returns {Promise<void>}
   */
  async handleResponse(response) {
    const data = response.data;

    if (data.streamUrl) {
      return await this.handleSinglePipelineResponse(data);
    } else {
      console.error('[ResponseHandler] Unknown response format:', data);
    }
  }

  /**
   * Handle single pipeline response - all content via SSE.
   * @param {Object} data - Response data with streamUrl
   * @returns {Promise<void>}
   */
  async handleSinglePipelineResponse(data) {
    const { streamUrl } = data;

    if (!streamUrl) {
      console.error('[ResponseHandler] No streamUrl in response');
      return;
    }

    // Extract jobId from streamUrl for event correlation
    // URL format: /api/jobs/{jobId}/stream
    const parts = streamUrl.split('/');
    const jobId = parts[parts.length - 2]; // Second to last segment
    this.currentJobId = jobId;
    // Reset per-job tracking (seq dedupe, watchdog, completion flag, etc.)
    this._resetStreamState();

    // Create EventSource for SSE connection
    const fullUrl = EVENTSOURCE_BASE_URL + streamUrl;
    const eventSource = new EventSource(fullUrl, { withCredentials: true });

    // Initialize streaming message container (pass stream for stop functionality)
    this.chatManager.startStreamingResponse(jobId, eventSource);

    return await this.connectToStream(eventSource, jobId);
  }

  /**
   * Connect to SSE stream and process chunks.
   * @param {EventSource} eventSource - SSE EventSource instance
   * @param {string} jobId - Job ID for event correlation
   * @returns {Promise<void>}
   */
  async connectToStream(eventSource, jobId) {
    // Fix B: No-message watchdog. If the stream stays silent longer than
    // this (across any number of native EventSource reconnect attempts),
    // give up and fall back to polling. A single transient blip resets
    // when the next message arrives.
    const WATCHDOG_MS = 15000;

    return new Promise((resolve, reject) => {
      // Run the polling fallback exactly once per stream. Consolidates the
      // cleanup + fallback + event emission that both the watchdog and the
      // hard-error path need. IMPORTANT: fallbackToPoll is awaited so
      // callers like ModeOrchestrator that `await handleResponse(...)`
      // only unblock once the job has truly completed — resolving eagerly
      // would let callers race ahead and render subsequent UI (e.g. a
      // questionnaire launcher) before the LLM message text lands,
      // producing out-of-order chat content.
      //
      // NOTE: This preserves the tactical ordering patch for the out-of-
      // order rendering race. The architecturally correct fix ("Option B")
      // is to move the QuestionnaireLauncher into the content plan itself
      // so ContentPlanExecutor owns ordering end-to-end. Deferred — see
      // the long-form comment in ModeOrchestrator.js around the
      // `// SSE path: LLM-generated intro via Haiku content plan` block.
      const triggerFallback = async (reason) => {
        if (this._fallbackTriggered) return;
        this._fallbackTriggered = true;
        if (this._watchdogTimer) {
          clearTimeout(this._watchdogTimer);
          this._watchdogTimer = null;
        }
        try { eventSource.close(); } catch { /* already closed */ }
        this.currentJobId = null;

        console.warn('[ResponseHandler] SSE → polling fallback', {
          jobId,
          reason,
          readyState: eventSource.readyState,
          elapsedMs: Date.now() - this._streamOpenedAt,
          silentMs: Date.now() - this._lastMessageAt,
          completedReceived: this._completedReceived
        });

        if (!this.chatManager.fallbackToPoll) {
          // Terminal with no render: nothing downstream will ever flip or hide
          // the indicator (a Case-B re-armed waiting phase would spin forever).
          this.chatManager.deactivateThinkingIndicator?.();
          resolve({ response: null, pendingActions: [], data: null });
          return;
        }

        try {
          const pollResult = await this.chatManager.fallbackToPoll(jobId);
          if (pollResult) {
            this.eventBus.emit('stream:complete', {
              jobId,
              response: pollResult.response,
              pendingActions: pollResult.pendingActions || [],
              data: pollResult.data
            });
            resolve(pollResult);
            return;
          }
        } catch (pollErr) {
          console.error('[ResponseHandler] Poll fallback failed:', pollErr);
          this.eventBus.emit('stream:failed', {
            jobId,
            error: pollErr.message,
            data: null
          });
        }
        // Falsy pollResult / poll failure: same terminal-with-no-render shape.
        this.chatManager.deactivateThinkingIndicator?.();
        resolve({ response: null, pendingActions: [], data: null });
      };

      const armWatchdog = () => {
        if (this._watchdogTimer) clearTimeout(this._watchdogTimer);
        this._watchdogTimer = setTimeout(() => {
          if (this._completedReceived || this._fallbackTriggered) return;
          triggerFallback(`no-message watchdog (${Date.now() - this._lastMessageAt}ms silent)`);
        }, WATCHDOG_MS);
      };

      armWatchdog();

      eventSource.onmessage = async (event) => {
        this._lastMessageAt = Date.now();
        armWatchdog(); // Any progress resets the watchdog
        try {
          const data = JSON.parse(event.data);
          await this.processChunk(data, jobId, eventSource, resolve, reject);
        } catch (error) {
          console.error('[ResponseHandler] Error processing chunk:', error);
        }
      };

      // Fix B + Fix E: don't treat onerror as immediately fatal, and log
      // with structured context.
      //
      // Why: EventSource fires `error` as part of its normal teardown
      // *after* the server closes the connection post-COMPLETED (see
      // backend chat.js:83 — the SSE route closes all clients on terminal
      // status). The previous implementation logged those benign
      // post-complete closes as errors and eagerly jumped to polling on
      // real errors, preventing EventSource's native reconnect from
      // recovering transient network blips.
      //
      // New behavior:
      //   - completedReceived → benign close, debug-log and return.
      //   - readyState === CLOSED → unrecoverable, trigger fallback now.
      //   - readyState === CONNECTING → native reconnect in progress; log
      //     and let the watchdog decide if it drags on too long.
      eventSource.onerror = (error) => {
        const ctx = {
          jobId,
          readyState: eventSource.readyState, // 0=CONNECTING 1=OPEN 2=CLOSED
          elapsedMs: Date.now() - this._streamOpenedAt,
          silentMs: Date.now() - this._lastMessageAt,
          completedReceived: this._completedReceived
        };

        if (this._completedReceived) {
          console.debug('[ResponseHandler] SSE closed post-complete', ctx);
          return;
        }

        if (eventSource.readyState === 2 /* CLOSED */) {
          console.error('[ResponseHandler] SSE closed without completion', ctx, error);
          triggerFallback('readyState=CLOSED');
          return;
        }

        // CONNECTING — EventSource is trying to reconnect. Let it; the
        // watchdog will catch it if it doesn't recover.
        console.warn('[ResponseHandler] SSE transient error — awaiting reconnect', ctx);
      };
    });
  }

  /**
   * Process a single chunk from SSE stream.
   * @param {Object} data - Chunk data
   * @param {string} jobId - Job ID
   * @param {EventSource} eventSource - SSE connection
   * @param {Function} resolve - Promise resolve
   * @param {Function} reject - Promise reject
   * @returns {Promise<void>}
   */
  async processChunk(data, jobId, eventSource, resolve, reject) {
    // Reject stale delivery from a stream that has yielded to polling or lost
    // ownership to a newer job. This closes the watchdog-poll + queued-SSE
    // double-finalize race without affecting direct unit-level chunk handling.
    if (
      (this._fallbackTriggered && this.currentJobId !== jobId)
      || (this.currentJobId && this.currentJobId !== jobId)
    ) {
      return;
    }

    // Belt-and-suspenders dedupe: every chunk emitted by ChunkEmitter carries
    // a monotonic per-job `seq`. The backend SSE handler already dedupes, but
    // we drop any seq we've already seen here too, in case a duplicate slips
    // through (e.g., after a future change to the streaming pipeline). Job
    // status updates from the `job:` channel don't have `seq` and are not
    // affected by this check.
    if (typeof data.seq === 'number') {
      if (data.seq <= this._lastSeenSeq) return;
      this._lastSeenSeq = data.seq;
    }

    switch (data.type) {
      case 'chunk':
        // LLM text chunk - append to current message
        this.eventBus.emit('stream:chunk', { jobId, content: data.content });
        break;

      case 'message':
        // Static message - route through unified pipeline for consistent rendering
        // Uses the streaming renderer's block-level fade-in animations
        this.eventBus.emit('stream:chunk', { jobId, content: data.text, messageType: data.messageType });
        break;

      case 'component':
        // Component - render via event system
        this.eventBus.emit('component:requested', {
          type: data.component,
          data: data.data
        });
        break;

      case 'artifact':
        // Artifact - render via event system (treatment plans, insights, charts)
        this.eventBus.emit('component:artifact-requested', {
          type: data.artifact,
          data: data.data
        });
        break;

      case 'state':
        // State update - merge into frontend state
        this.eventBus.emit('state:update', {
          jobId,
          updates: data.updates
        });
        break;

      case 'action': {
        // ChunkEmitter.emitAction spreads payload fields top-level (documented at
        // project-docs/components/01-messaging-pipeline.md). LLM tool calls reach
        // this same eventBus event via ChatManager with { type, value, input }
        // shape. Derive `input` here so ActionRouter consumers can read
        // data.input.field regardless of which path produced the action.
        const {
          type: _type,
          action: _action,
          jobId: _jobId,
          timestamp: _timestamp,
          seq: _seq,
          payload,
          data: dataField,
          ...spreadParams
        } = data;
        this.eventBus.emit('action:llm-triggered', {
          type: data.action,
          value: payload || dataField,
          input: payload || dataField || spreadParams
        });
        break;
      }

      case 'error':
        // Non-critical error - log for visibility, don't fail the stream
        console.warn(`[ResponseHandler] Non-critical error in ${data.itemType}:`, data.message);
        this.eventBus.emit('stream:item-error', {
          jobId,
          itemType: data.itemType,
          message: data.message
        });
        break;

      default:
        // Handle job status updates (from job:${jobId} channel)
        if (data.status === 'COMPLETED') {
          this._completedReceived = true;
          if (this._watchdogTimer) {
            clearTimeout(this._watchdogTimer);
            this._watchdogTimer = null;
          }
          this.chatManager?.handleTerminalStatus?.(jobId, {
            status: 'COMPLETED',
            response: data.response,
            pendingActions: data.metadata?.pendingActions || [],
            // Backend's ONE Phase 3 derivation, carried on the SSE transport via
            // published `metadata` (app-tools plan Phase 3.6 — deterministic
            // stashed-button auto-restore). See fallbackToPoll for the
            // poll-transport read of the same tag from `job.context`.
            appSupport: data.metadata?.appSupport === true,
            data,
            source: 'sse'
          });
          this.eventBus.emit('stream:complete', {
            jobId,
            response: data.response,
            pendingActions: data.metadata?.pendingActions || [],
            data
          });
          eventSource.close();
          this.currentJobId = null;
          resolve({ response: data.response, pendingActions: data.metadata?.pendingActions || [], data });
        } else if (data.status === 'STOPPED_BY_USER') {
          this._completedReceived = true; // terminal — treat as benign close
          if (this._watchdogTimer) {
            clearTimeout(this._watchdogTimer);
            this._watchdogTimer = null;
          }
          this.chatManager?.handleTerminalStatus?.(jobId, {
            status: 'STOPPED_BY_USER',
            data,
            source: 'sse'
          });
          eventSource.close();
          this.currentJobId = null;
          resolve({ response: null, pendingActions: [], data: null });
        } else if (data.status === 'FAILED') {
          this._completedReceived = true; // terminal — suppress onerror noise
          if (this._watchdogTimer) {
            clearTimeout(this._watchdogTimer);
            this._watchdogTimer = null;
          }
          // Render the failed bubble with a Retry button so the SSE-direct
          // FAILED path matches the polling-fallback FAILED path. We resolve
          // (rather than reject) so ModeOrchestrator's catch doesn't also
          // fire its own generic displayError and double up the error UI.
          this.chatManager?.handleTerminalStatus?.(jobId, {
            status: 'FAILED',
            errorCode: data.errorCode,
            userMessage: data.error,
            requestId: data.requestId,
            data,
            source: 'sse'
          });
          eventSource.close();
          this.currentJobId = null;
          // Notify state-machine listeners only after render/close/finalize.
          // Listener failures must never strand the input lock.
          try {
            this.eventBus.emit('stream:failed', {
              jobId,
              error: data.error,
              errorCode: data.errorCode,
              requestId: data.requestId,
              data
            });
          } catch (listenerError) {
            console.error('[ResponseHandler] stream:failed listener error:', listenerError);
          }
          resolve({ response: null, pendingActions: [], data });
        }
        // Ignore PROCESSING, QUEUED status updates
    }
  }
}
